import { NetworkType } from 'abis/set/sets'
import { STAKING_ROUTER_ADDRESS } from 'app-constants'
import Big from 'big.js'
import { chainIdAtom, userAccountAtom } from 'context/account'
import { CurrencyMetadata } from 'indices-list'
import { Atom, atom } from 'jotai'
import { productMetadataAtom } from 'pages/index-details/storage'
import { ProductType } from 'sdk'
import { approveAtom } from 'services/query-v2/approve'
import { permitAtom } from 'services/query-v2/product-v2/permit'
import { PermitSignStore } from 'services/query-v2/product-v2/permit-utils'
import { Address } from 'viem'
import {
  executionAtom,
  input,
  inputCurrencyAtom,
  nativeCurrency,
  outputAmountAtom,
  outputCurrencyAtom,
  v2ExtraAtoms,
} from './setup'
import { isApprovalRequire } from './utils'

//TRANSACTIONAL STATE STORAGE

export enum InvestStateErrors {
  Disconnected = 'connection-error',
  UnsupportedNetwork = 'unsupported-network',
  EscrowPending = 'escrow-pending',
  EscrowRecover = 'escrow-recover',
  RebalancePending = 'rebalance-pending',
  ///
  InsufficientAssetBalance = 'insufficient-asset-balance',
  InsufficientAllowance = 'insufficient-allowance',
  ZeroAmount = 'zero-amount',
}

export type SuccessPayload = {
  account: Address
  sellAmount: Big
  buyAmount?: Big
  balance: Big
  inputCurrency: CurrencyMetadata
  outputCurrency: CurrencyMetadata
  isApproval: boolean
  takerAddress: Address
  nativeBalance: Big
  productAddress: Address
  isRetry: boolean
  isUserContract: boolean
  permit?: PermitSignStore
  stopProcessing?: () => void
  clearState?: () => void
}

type InvestStateSuccess = {
  type: 'success'
  payload: SuccessPayload
}

type InvestStateError = {
  type: 'error'
  payload: InvestStateErrors
  chainToSwitch?: NetworkType
  isRetry?: boolean
}

type EscrowStateSuccess = {
  type: 'success'
  payload: SuccessPayload
}

export type InvestStateStatus = InvestStateSuccess | InvestStateError

export type EscrowStateStatus = EscrowStateSuccess | InvestStateError

type DefaultConnectionStateProps = {
  network: NetworkType
  userAddress: Address
  type: 'success'
}

export type DefaultInputStateProps = {
  balance: Big
  sellAmount: Big
  buyAmount?: Big
  allowance: Big
  inputCurrency: CurrencyMetadata
  outputCurrency: CurrencyMetadata
  takerAddress: Address
  nativeBalance: Big
  productAddress: Address
  isRetry: boolean
  isUserContract: boolean
  type: 'success'
  permit?: PermitSignStore
}

export type TransactionConfirmInterface = {
  type: 'transaction-confirm'
  payload: InvestStateStatus
}

export type DispatchInvestStateActions = TransactionConfirmInterface

export const transactionConfirm = atom(null, (get, _, update: InvestStateSuccess) => {
  const { mutate: approveFunction } = get(approveAtom)
  const { mutate: permitFunction } = get(permitAtom)

  const { isApproval: isApprovalRequired, inputCurrency, permit, isUserContract } = update.payload
  const isNativeCurrency = inputCurrency.type === 'native'

  if (isNativeCurrency) {
    const execute = get(get(executionAtom))
    execute.mutate(update.payload)
  } else {
    // INFO: inputCurrency always is a erc-20 token here

    if (!isUserContract && permit === undefined && inputCurrency.permit !== undefined) {
      // INFO: if user is NOT a multisig acc & without permit & token is permittable
      permitFunction(update.payload)
    } else if (
      (isUserContract && isApprovalRequired) ||
      (inputCurrency.permit === undefined && isApprovalRequired)
    ) {
      // INFO: if user IS a multisig acc & approval required OR token is not permittable & approval required
      approveFunction(update.payload)
    } else {
      const execute = get(get(executionAtom))
      execute.mutate(update.payload)
    }
  }
})

const connectionStateAtom = atom<InvestStateError | DefaultConnectionStateProps>((get) => {
  const userAddress = get(userAccountAtom)
  const product = get(productMetadataAtom)
  const connectedChainId = get(chainIdAtom)
  if (!userAddress) {
    return {
      payload: InvestStateErrors.Disconnected,
      type: 'error',
    }
  }
  if (connectedChainId !== product.network) {
    return {
      payload: InvestStateErrors.UnsupportedNetwork,
      type: 'error',
      chainToSwitch: product.network,
    }
  }
  return {
    type: 'success',
    network: connectedChainId,
    userAddress,
  }
})

const inputStateAtom = atom<InvestStateError | DefaultInputStateProps>((get) => {
  const balance = get(input.balance)
  const nativeBalance = get(nativeCurrency.balance)
  const sellAmount = get(input.amount)
  const currentInputAsset = get(inputCurrencyAtom)
  const currentOutputAsset = get(outputCurrencyAtom)
  const allowance = get(input.allowance)
  const allowanceTaker = get(input.allowanceTaker)
  const buyAmount = get(outputAmountAtom)
  const metadata = get(productMetadataAtom)

  const isEscrowFilled = get(v2ExtraAtoms.isEscrowFilled)
  const isUserContract = get(v2ExtraAtoms.isUserContract)
  const permit = get(v2ExtraAtoms.permit)
  const isReweightRunning = get(v2ExtraAtoms.isRebalance)
  if (metadata.productType === ProductType.INDEX_V2 && isReweightRunning) {
    return {
      payload: InvestStateErrors.RebalancePending,
      type: 'error',
    }
  }
  const takerAddress =
    metadata.productType === ProductType.INDEX_V2
      ? allowanceTaker
      : currentInputAsset.type === 'PHTR' || currentInputAsset.type === 'staked-PHTR'
        ? STAKING_ROUTER_ADDRESS
        : currentInputAsset.type === 'token' && currentOutputAsset.type === 'index-v2'
          ? currentOutputAsset.address
          : allowanceTaker

  if (metadata.productType === ProductType.INDEX_V2 && isEscrowFilled) {
    return {
      type: 'success',
      balance,
      sellAmount,
      buyAmount,
      allowance,
      takerAddress,
      inputCurrency: currentInputAsset,
      outputCurrency: currentOutputAsset,
      nativeBalance: nativeBalance,
      productAddress: metadata.address,
      isUserContract,
      isRetry: true,
      permit,
    }
  }
  if (sellAmount.eq(0)) {
    return {
      payload: InvestStateErrors.ZeroAmount,
      type: 'error',
      isRetry: isEscrowFilled,
    }
  }

  if (balance.lt(sellAmount)) {
    return {
      payload: InvestStateErrors.InsufficientAssetBalance,
      type: 'error',
    }
  }

  return {
    type: 'success',
    balance,
    sellAmount,
    buyAmount,
    allowance,
    takerAddress,
    inputCurrency: currentInputAsset,
    outputCurrency: currentOutputAsset,
    nativeBalance: nativeBalance,
    productAddress: metadata.address,
    isRetry: false,
    permit,
    isUserContract,
  }
})

export const investStateAtom: Atom<InvestStateStatus> = atom((get) => {
  const connectionState = get(connectionStateAtom)
  if (connectionState.type !== 'success') {
    return connectionState
  }
  // const escrowState = get(escrowStateAtom)

  // if (escrowState.type !== 'success') {
  //   return escrowState
  // }

  const inputState = get(inputStateAtom)
  if (inputState.type !== 'success') {
    return inputState
  }
  return {
    type: 'success',
    payload: {
      account: connectionState.userAddress,
      sellAmount: inputState.sellAmount,
      buyAmount: inputState.buyAmount,
      balance: inputState.balance,
      isApproval: isApprovalRequire(inputState),
      inputCurrency: inputState.inputCurrency,
      outputCurrency: inputState.outputCurrency,
      takerAddress: inputState.takerAddress,
      nativeBalance: inputState.nativeBalance,
      productAddress: inputState.productAddress,
      isRetry: inputState.isRetry,
      permit: inputState.permit,
      isUserContract: inputState.isUserContract,
    },
  }
})
